import { NO_VALUE, formatInt, formatPct } from '../format.ts'

/**
 * Leitura defensiva do `system_snapshot` do agente. Todo campo é opcional e o
 * schema pode crescer: o que falta vira "—" (nunca 0) e o que é desconhecido é
 * ignorado.
 */

type Rec = Record<string, unknown>

export function rec(v: unknown): Rec | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Rec) : null
}

export function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

export function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return NO_VALUE
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
  let value = bytes
  let i = 0
  while (Math.abs(value) >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${i === 0 ? formatInt(value) : value.toFixed(1).replace('.', ',')} ${units[i]}`
}

export function formatUptime(seconds: number | null): string {
  if (seconds === null) return NO_VALUE
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d} d ${h} h`
  if (h > 0) return `${h} h ${m} min`
  return m > 0 ? `${m} min` : `${Math.floor(seconds)} s`
}

export interface SnapshotRow {
  label: string
  value: string
}

export interface SnapshotSection {
  title: string
  rows: SnapshotRow[]
}

const pct = (v: number | null): string => (v === null ? NO_VALUE : formatPct(v, 1))
const int = (v: number | null): string => (v === null ? NO_VALUE : formatInt(v))
const text = (v: string | null): string => v ?? NO_VALUE

/** `null` quando o conteúdo não é um objeto — quem chama mostra o JSON cru. */
export function readSnapshot(content: unknown): SnapshotSection[] | null {
  const root = rec(content)
  if (root === null) return null

  const host = rec(root.host)
  const cpu = rec(root.cpu)
  const mem = rec(root.memory)
  const net = rec(root.network)
  const conns = rec(net?.connections)
  const agent = rec(root.agent)
  const events = rec(agent?.events)
  const conn = rec(agent?.connection)

  const sections: SnapshotSection[] = [
    {
      title: 'Máquina',
      rows: [
        { label: 'Coletado em', value: text(str(root.collected_at)) },
        { label: 'Hostname', value: text(str(host?.hostname) ?? str(host?.name)) },
        { label: 'Sistema', value: text(str(host?.os) ?? str(host?.os_version)) },
        { label: 'Schema', value: text(str(root.schema_version) ?? (num(root.schema_version)?.toString() ?? null)) },
      ],
    },
    {
      title: 'CPU e memória',
      rows: [
        { label: 'CPU em uso', value: pct(num(cpu?.used_percent) ?? num(cpu?.percent)) },
        { label: 'Núcleos', value: int(num(cpu?.cores) ?? num(cpu?.logical_cores)) },
        { label: 'RAM total', value: formatBytes(num(mem?.total_bytes)) },
        { label: 'RAM disponível', value: formatBytes(num(mem?.available_bytes)) },
        { label: 'RAM em uso', value: pct(num(mem?.used_percent)) },
      ],
    },
    {
      title: 'Agente',
      rows: [
        { label: 'Versão', value: text(str(agent?.version)) },
        { label: 'Uptime', value: formatUptime(num(agent?.uptime_seconds)) },
        { label: 'CPU do agente', value: pct(num(agent?.cpu_percent)) },
        { label: 'Memória do agente (RSS)', value: formatBytes(num(agent?.memory_rss_bytes)) },
        { label: 'Threads', value: int(num(agent?.threads)) },
        { label: 'Eventos coletados', value: int(num(events?.collected_total)) },
        { label: 'Eventos enviados', value: int(num(events?.sent_total)) },
        { label: 'Eventos descartados', value: int(num(events?.dropped_total)) },
        {
          label: 'Fila',
          value:
            num(events?.queue_depth) === null
              ? NO_VALUE
              : `${int(num(events?.queue_depth))}${num(events?.queue_capacity) === null ? '' : ` de ${int(num(events?.queue_capacity))}`}`,
        },
        { label: 'Athena alcançável', value: boolText(conn?.athena_reachable) },
        { label: 'Isolado', value: boolText(conn?.isolated) },
      ],
    },
    {
      title: 'Conexões',
      rows: [
        { label: 'Total', value: int(num(conns?.total)) },
        { label: 'TCP estabelecidas', value: int(num(conns?.tcp_established)) },
        ...byState(rec(conns?.by_state)),
      ],
    },
  ]

  return sections
}

function boolText(v: unknown): string {
  return typeof v === 'boolean' ? (v ? 'sim' : 'não') : NO_VALUE
}

function byState(states: Rec | null): SnapshotRow[] {
  if (states === null) return []
  return Object.entries(states)
    .map(([k, v]) => ({ k, n: num(v) }))
    .filter((e): e is { k: string; n: number } => e.n !== null)
    .map((e) => ({ label: `Estado ${e.k}`, value: formatInt(e.n) }))
}
